import { useParameterStore } from "@/frontend/store/ParameterStore";
import { useTreeStore } from "@/frontend/store/TreeStore";
import { ContainerTreeObject } from "@/frontend/types/navigationTypes";
import { I18n } from "@/i18n/I18n";
import { Status } from "@/types/containerTypes";
import { ParameterSubjectType } from "@/types/parameterTypes";
import { useEffect, useState } from "react";
import { useShallow } from "zustand/react/shallow";
import { RunPlaceholder } from "../RunPlaceholder";
import { ScenarioRun } from "./ScenarioRun";
import { StepItemRunBody } from "./StepItemRunBody";
import { Step } from "@/backend/models/containers/step";
import { StepItemRunHeader } from "./StepItemRunHeader";
import { RepeatingStepRun } from "./RepeatingStepRun";
import { useStepStatusColor } from "@/frontend/hooks/useStepStatusColor";

export function StepRun(props: { stepId: string }) {
  const { stepId } = props;

  const containerItem = useTreeStore((state) => { return state.containerLookup[stepId]; });
  const { container } = containerItem;
  const scenarios = containerItem.children.filter((child) => child.container.isScenario());
  const selectedScenario = scenarios.find((scenario) => scenario.container.id === (containerItem.container as Step)?.selectedScenarioId) || scenarios[0];
  const observer = useTreeStore((state) => state.observer);
  const parameters = useParameterStore(useShallow(state => state.parametersForSubject(stepId, ParameterSubjectType.STEP)));
  const cardColorClass = useStepStatusColor(container);

  useEffect(() => {
    const observedItem = document.getElementById(`${stepId}`);
    if (observer && observedItem) {
      observer.observe(observedItem as HTMLElement);
    }
    return () => {
      if (observer && observedItem) {
        observer.unobserve(observedItem as HTMLElement);
      }
    };
  }, [observer, stepId]);

  if (!containerItem?.loaded) {
    return (
      <div aria-label={I18n.get('loadingStepItem')} id={stepId} className="card-header bg-body-secondary">
        <RunPlaceholder />
        <ScenarioRun scenarioNode={selectedScenario} />
        {parameters.map((param) => <div id={param.id} key={param.id} />)}
      </div>
    );
  }

  if (!(containerItem.container instanceof Step)) return null;

  const updateStatusWrapper = () => {
    document.getElementById(`${stepId}`)?.scrollIntoView({ behavior: 'instant' });
  };

  return (
    <div id={stepId} className={`card observed-element ${cardColorClass}`}>
      { containerItem.container.isStep() && <NormalStep containerItem={containerItem} onUpdateStatus={updateStatusWrapper} /> }
      { containerItem.container.isRepeatingStep() && <RepeatingStepRun initItem={containerItem} finishStep={updateStatusWrapper} collapsible /> }
    </div>
  );
}

function NormalStep(props: { containerItem: ContainerTreeObject, onUpdateStatus: () => void }) {
  const { containerItem, onUpdateStatus } = props;

  const [showStep, setShowStep] = useState(containerItem.container.status === Status.PENDING);

  const updateStatusWrapper = () => {
    onUpdateStatus();
    setShowStep(false);
  };

  return (
    <>
        <StepItemRunHeader
          step={containerItem.container as Step}
          setShowStep={setShowStep}
          showStep={showStep}
        />
        { showStep && containerItem.container.isStep() && <StepItemRunBody step={containerItem} onUpdateStatus={updateStatusWrapper} /> }
    </>
  );
}
